import { marked, Tokens } from 'marked';
import DOMPurify from 'dompurify';

/**
 * Configure marked for proper formatting
 * - breaks: true - Convert single line breaks to <br>
 * - gfm: true - GitHub Flavored Markdown
 * - Custom link renderer for new tab behavior
 */
marked.use({
    breaks: true,
    gfm: true,
    renderer: {
        link({ href, title, text }: Tokens.Link) {
            const titleAttr = title ? ` title="${title}"` : '';
            // Use _self to open in same tab
            // Use original link text instead of forcing "Clique aqui!"
            return `<a href="${href}" target="_top" rel="noopener noreferrer"${titleAttr} class="markdown-link">${text}</a>`;
        },
    },
});

/**
 * Parse markdown and sanitize HTML
 * @param markdown - Raw markdown string
 * @returns Sanitized HTML string
 */
export function parseMarkdown(markdown: string): string {
    // Preprocess: fix malformed links like "[Link: texto](url)" -> "[texto](url)"
    // Also handles "[Link:" at start of line followed by link
    let cleaned = markdown
        // Fix "[Link: [texto](url)" -> "[texto](url)"
        .replace(/\[Links?:\s*\[([^\]]+)\]\(([^)]+)\)\]?/gi, '[$1]($2)')
        // Fix "[Link: texto](url)" -> "[texto](url)"  
        .replace(/\[Links?:\s*([^\]]+)\]\(([^)]+)\)\]?/gi, '[$1]($2)')
        // Fix standalone "[Link: url" or "[Link: url]" -> just the URL
        .replace(/\[Links?:\s*(https?:\/\/[^\s\]]+)\]?/gi, '$1');

    // Parse markdown to HTML
    const html = marked.parse(cleaned, { async: false }) as string;

    // Sanitize to prevent XSS
    const clean = DOMPurify.sanitize(html, {
        ADD_ATTR: ['target', 'rel'],
    });

    return clean;
}
